import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { isValidYoutubeUrl } from '../utils/validation';
import { auth } from '../firebase';

function EditVideoModal({ isOpen, onClose, video, onSuccess }) {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [error, setError] = useState('');
    const [formData, setFormData] = useState({
        title_en: '',
        title_tl: '',
        description_en: '',
        description_tl: '',
        category: '',
        youtubeUrl: '',
        customThumbnail: '',
        thumbnailColor: 'linear-gradient(135deg, #c7252b, #e02d34)'
    });

    useEffect(() => {
        if (video && isOpen) {
            // Extract bilingual data
            const titleEn = typeof video.title === 'object' ? video.title.en : video.title || '';
            const titleTl = typeof video.title === 'object' ? video.title.tl : '';
            const descEn = typeof video.description === 'object' ? video.description.en : video.description || '';
            const descTl = typeof video.description === 'object' ? video.description.tl : '';

            // Convert embed URL back to watch URL if needed
            let youtubeUrl = video.src || '';
            if (youtubeUrl.includes('/embed/')) {
                const videoId = youtubeUrl.split('/embed/')[1]?.split('?')[0];
                if (videoId) {
                    youtubeUrl = `https://www.youtube.com/watch?v=${videoId}`;
                }
            }

            setFormData({
                title_en: titleEn,
                title_tl: titleTl,
                description_en: descEn,
                description_tl: descTl,
                category: video.category || '',
                youtubeUrl: youtubeUrl,
                customThumbnail: video.customThumbnail || '',
                thumbnailColor: video.thumbnailColor || 'linear-gradient(135deg, #c7252b, #e02d34)'
            });
            setError('');
            setShowDeleteConfirm(false);
        }
    }, [video, isOpen]);

    if (!isOpen || !video) return null;

    const categories = ['Promotional', 'Tutorials', 'Events', 'Other'];

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (error) setError('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!formData.title_en || !formData.category) {
            setError(t('video_fill_required'));
            return;
        }

        // Only validate YouTube URL if it's a YouTube video
        if (video.type === 'youtube' && formData.youtubeUrl && !isValidYoutubeUrl(formData.youtubeUrl)) {
            setError(t('video_invalid_url'));
            return;
        }

        setLoading(true);

        try {
            let embedUrl = formData.youtubeUrl;
            if (formData.youtubeUrl) {
                const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
                const match = formData.youtubeUrl.match(regExp);

                if (match && match[2].length === 11) {
                    embedUrl = `https://www.youtube.com/embed/${match[2]}`;
                }
            }

            const token = await auth.currentUser.getIdToken();
            const response = await fetch(`/api/videos/${video.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    title: {
                        en: formData.title_en,
                        tl: formData.title_tl
                    },
                    description: {
                        en: formData.description_en,
                        tl: formData.description_tl
                    },
                    category: formData.category,
                    youtubeUrl: formData.youtubeUrl,
                    customThumbnail: formData.customThumbnail,
                    thumbnailColor: formData.thumbnailColor
                })
            });

            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.error || 'Failed to update video');
            }

            if (onSuccess) onSuccess();
            onClose();
        } catch (err) {
            console.error('Error updating video:', err);
            setError(err.message || t('video_edit_failed'));
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        setDeleting(true);
        try {
            const token = await auth.currentUser.getIdToken();
            const response = await fetch(`/api/videos/${video.id}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.error || 'Failed to delete video');
            }

            if (onSuccess) onSuccess();
            onClose();
        } catch (err) {
            console.error('Error deleting video:', err);
            setError(err.message || t('video_delete_failed'));
        } finally {
            setDeleting(false);
        }
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content modal-large" onClick={e => e.stopPropagation()}>
                <button className="modal-close-btn" onClick={onClose}>×</button>
                <h3 className="modal-title">{t('video_edit_modal_title')}</h3>
                <p className="modal-subtitle">{t('video_edit_modal_subtitle')}</p>

                {error && <div className="error-banner">{error}</div>}

                {showDeleteConfirm ? (
                    <div className="delete-confirm-container">
                        <div className="delete-confirm-icon">⚠️</div>
                        <h4>{t('video_delete_confirm_title')}</h4>
                        <p>{t('video_delete_confirm_message')}</p>
                        <div className="modal-actions">
                            <button
                                type="button"
                                className="btn-outline-modern"
                                onClick={() => setShowDeleteConfirm(false)}
                                disabled={deleting}
                            >
                                {t('cancel')}
                            </button>
                            <button
                                type="button"
                                className="btn-danger-modern"
                                onClick={handleDelete}
                                disabled={deleting}
                            >
                                {deleting ? t('video_deleting') : t('video_delete_confirm_btn')}
                            </button>
                        </div>
                    </div>
                ) : (
                    <form className="modal-form" onSubmit={handleSubmit}>
                        {/* Bilingual Content Section */}
                        <div className="bilingual-section compact">
                            <h4 className="bilingual-section-title">
                                🌐 {t('bilingual_section_title')}
                            </h4>
                            <p className="bilingual-section-help" style={{ fontSize: '0.85rem' }}>{t('bilingual_section_help')}</p>

                            {/* English Content */}
                            <div className="language-block compact">
                                <div className="language-header">
                                    <span className="language-flag">🇺🇸</span>
                                    <span className="language-label">{t('english_content')}</span>
                                </div>

                                <div className="form-group">
                                    <label>{t('video_title_en')} *</label>
                                    <input
                                        type="text"
                                        name="title_en"
                                        className="form-input"
                                        value={formData.title_en}
                                        onChange={handleChange}
                                        placeholder={t('video_title_en_placeholder')}
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label>{t('video_desc_en')}</label>
                                    <textarea
                                        name="description_en"
                                        className="form-input form-textarea"
                                        value={formData.description_en}
                                        onChange={handleChange}
                                        placeholder={t('video_desc_en_placeholder')}
                                        rows="2"
                                    />
                                </div>
                            </div>

                            {/* Tagalog Content */}
                            <div className="language-block compact">
                                <div className="language-header">
                                    <span className="language-flag">🇵🇭</span>
                                    <span className="language-label">{t('tagalog_content')}</span>
                                    <span className="optional-badge">{t('optional')}</span>
                                </div>

                                <div className="form-group">
                                    <label>{t('video_title_tl')}</label>
                                    <input
                                        type="text"
                                        name="title_tl"
                                        className="form-input"
                                        value={formData.title_tl}
                                        onChange={handleChange}
                                        placeholder={t('video_title_tl_placeholder')}
                                    />
                                </div>

                                <div className="form-group">
                                    <label>{t('video_desc_tl')}</label>
                                    <textarea
                                        name="description_tl"
                                        className="form-input form-textarea"
                                        value={formData.description_tl}
                                        onChange={handleChange}
                                        placeholder={t('video_desc_tl_placeholder')}
                                        rows="2"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Category */}
                        <div className="form-group">
                            <label>{t('video_category')} *</label>
                            <select
                                name="category"
                                className="form-input"
                                value={formData.category}
                                onChange={handleChange}
                                required
                            >
                                <option value="">{t('video_select_category')}</option>
                                {categories.map(cat => (
                                    <option key={cat} value={cat}>{cat}</option>
                                ))}
                            </select>
                        </div>

                        {/* YouTube URL - only for YouTube videos */}
                        {video.type === 'youtube' && (
                            <div className="form-group">
                                <label>{t('video_youtube_url')}</label>
                                <input
                                    type="url"
                                    name="youtubeUrl"
                                    className="form-input"
                                    value={formData.youtubeUrl}
                                    onChange={handleChange}
                                    placeholder="https://www.youtube.com/watch?v=..."
                                />
                            </div>
                        )}

                        {/* Custom Thumbnail URL */}
                        <div className="form-group">
                            <label>{t('video_custom_thumbnail')}</label>
                            <input
                                type="url"
                                name="customThumbnail"
                                className="form-input"
                                value={formData.customThumbnail}
                                onChange={handleChange}
                                placeholder={t('video_custom_thumbnail_placeholder')}
                            />
                            <small className="form-help-text">{t('video_custom_thumbnail_help')}</small>
                        </div>

                        {/* Card Gradient */}
                        <div className="form-group">
                            <label>{t('video_card_gradient')}</label>
                            <select
                                name="thumbnailColor"
                                className="form-input"
                                value={formData.thumbnailColor}
                                onChange={handleChange}
                            >
                                <option value="linear-gradient(135deg, #c7252b, #e02d34)">Red (Default)</option>
                                <option value="linear-gradient(135deg, #f6cc25, #e6a821)">Gold</option>
                                <option value="linear-gradient(135deg, #1f4282, #00225e)">Blue</option>
                                <option value="linear-gradient(135deg, #4CAF50, #2E7D32)">Green</option>
                                <option value="linear-gradient(135deg, #9C27B0, #673AB7)">Purple</option>
                            </select>
                        </div>

                        <div className="modal-actions-split">
                            <button
                                type="button"
                                className="btn-danger-outline"
                                onClick={() => setShowDeleteConfirm(true)}
                                disabled={loading}
                            >
                                🗑️ {t('delete')}
                            </button>
                            <div className="modal-actions-right">
                                <button
                                    type="button"
                                    className="btn-outline-modern"
                                    onClick={onClose}
                                    disabled={loading}
                                >
                                    {t('cancel')}
                                </button>
                                <button
                                    type="submit"
                                    className="btn-primary-modern"
                                    disabled={loading}
                                >
                                    {loading ? t('video_saving') : t('save_changes')}
                                </button>
                            </div>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
}

export default EditVideoModal;
